import React, {useCallback, useEffect, useState} from "react";
import Button from "./Button";
import styled from "styled-components";

export type TriggerJson = {
    logic?: LogicNode;
};

type LogicNode =
    | { or: LogicNode[] }
    | { and: LogicNode[] }
    | ConditionNode;

type ConditionNode = {
    branch?: string;
    pathsChanged?: string[];
    commitMessage?: { includes?: string; excludes?: string; regex?: string };
    tag?: { matches: string };
    manual?: boolean;
    schedule?: string;
};

interface TriggerEditorProps {
    value: TriggerJson;
    onChange: (newValue: TriggerJson | null) => void;
}

export default function TriggerEditor({ value, onChange }: TriggerEditorProps) {

    const handleLogicChange = useCallback(
        (logic: LogicNode) => onChange({ ...value, logic }),
        [onChange, value]
    );

    return (
        <div
            style={{ padding: 16, background: "var(--content-box-bg)", borderRadius: 8 }}
        >
            <h3>Trigger-Conditions</h3>
            <LogicNodeEditor node={value.logic} onChange={handleLogicChange} canDelete={true} onDelete={() => onChange(null)} />
        </div>
    );
}



const LogicBox = styled.div`

    background-color: var(--content-box-bg);
    
    &.mouseover {
        background-color: var(--accent-blue-hover, #f0f0f0);
    }

`;

function LogicNodeEditor({
                             node,
                             onChange,
                             canDelete,
                             onDelete,
                         }: {
    node?: LogicNode;
    onChange: (n: LogicNode) => void;
    canDelete?: boolean;
    onDelete?: () => void;
}) {
    const [isHovered, setIsHovered] = useState(false);

    if (!node) {
        return (
            <div>
                <Button onClick={() => onChange({ and: [] })}>add AND-Group</Button>
                <Button onClick={() => onChange({ or: [] })} style={{ marginLeft: 8 }}>add OR-Group</Button>
            </div>
        );
    }

    if ("or" in node) {
        return (
            <GroupEditor
                type="or"
                nodes={node.or}
                onChange={ns => onChange({ or: ns })}
                onTypeChange={type => onChange({ [type]: node.or } as any)}
                canDelete={canDelete}
                onDelete={onDelete}
            />
        );
    }
    if ("and" in node) {
        return (
            <GroupEditor
                type="and"
                nodes={node.and}
                onChange={ns => onChange({ and: ns })}
                onTypeChange={type => onChange({ [type]: node.and } as any)}
                canDelete={canDelete}
                onDelete={onDelete}
            />
        );
    }
    return (
        <LogicBox
            className={"logicbox"+(isHovered ? " mouseover" : "")}
            style={{
                borderRadius: 4,
                padding: 12,
                boxShadow: "0 1px 4px var(--content-box-border)"
            }}
            onMouseOver={e => {
                if (e.currentTarget === findNextGroupBox(e.target as HTMLElement)) setIsHovered(true);
            }}
            onMouseOut={e => {
                if (e.currentTarget === findNextGroupBox(e.target as HTMLElement)) setIsHovered(false);
            }}
        >
            <ConditionEditor node={node} onChange={onChange} />
            {canDelete && (
                <Button style={{ color: "red", marginTop: 4 }} onClick={onDelete}>
                    Remove Condition
                </Button>
            )}
        </LogicBox>
    );
}

const GroupBox = styled.div`

    background-color: var(--content-box-bg);
    
    &.mouseover {
        background-color: var(--accent-blue-hover, #f0f0f0);
    }

`;

const findNextGroupBox = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    if (element.classList.contains("groupbox")) return element;
    if (element.classList.contains("logicbox")) return element;
    return findNextGroupBox(element.parentElement);
}

function GroupEditor({
                         type,
                         nodes,
                         onChange,
                         onTypeChange,
                         canDelete,
                         onDelete,
                     }: {
    type: "and" | "or";
    nodes: LogicNode[];
    onChange: (nodes: LogicNode[]) => void;
    onTypeChange: (type: "and" | "or") => void;
    canDelete?: boolean;
    onDelete?: () => void;
}) {
    const [isHovered, setIsHovered] = useState(false);
    const color = type === "and" ? "#0074d9" : "#ff9800";
    const addCondition = () => onChange([...nodes, {}]);
    const addAndGroup = () => onChange([...nodes, { and: [] }]);
    const addOrGroup = () => onChange([...nodes, { or: [] }]);

    return (
        <GroupBox
            className={'groupbox'+(isHovered ? " mouseover" : "")}
            style={{
                borderLeft: `3px solid ${color}`,
                margin: 8,
                paddingLeft: 12,
                boxShadow: "0 1px 4px var(--content-box-border)"
            }}
            onMouseOver={e => {
                if (e.currentTarget === findNextGroupBox(e.target as HTMLElement)) setIsHovered(true);
            }}
            onMouseOut={e => {
                if (e.currentTarget === findNextGroupBox(e.target as HTMLElement)) setIsHovered(false);
            }}
        >
            <div style={{ fontWeight: "bold", color, display: "flex", alignItems: "center" }}>
                {type.toUpperCase()}
                <Button
                    style={{ marginLeft: 8, fontSize: 12 }}
                    onClick={() => onTypeChange(type === "and" ? "or" : "and")}
                >
                    Switch to {type === "and" ? "OR" : "AND"}
                </Button>
                {canDelete && (
                    <Button
                        style={{ marginLeft: 8, color: "red", fontSize: 12 }}
                        onClick={onDelete}
                    >
                        remove Group
                    </Button>
                )}
            </div>
            {nodes.map((n, i) => (
                <LogicNodeEditor
                    key={i}
                    node={n}
                    onChange={newNode => {
                        const copy = [...nodes];
                        copy[i] = newNode;
                        onChange(copy);
                    }}
                    canDelete={true}
                    onDelete={() => onChange(nodes.filter((_, idx) => idx !== i))}
                />
            ))}
            <div style={{ marginTop: 4, display: 'flex' }}>
                <Button onClick={addCondition}>add Condition</Button>
                <Button onClick={addAndGroup} style={{ marginLeft: 8 }}>AND-Group</Button>
                <Button onClick={addOrGroup} style={{ marginLeft: 8 }}>OR-Group</Button>
            </div>
        </GroupBox>
    );
}

function ConditionEditor({
                             node,
                             onChange,
                         }: {
    node: ConditionNode;
    onChange: (n: ConditionNode) => void;
}) {
    return (
        <div style={{ borderRadius: 4, padding: 12 }}>
            <BranchField value={node.branch} onChange={v => onChange({ ...node, branch: v })} />
            <PathsChangedField value={node.pathsChanged} onChange={v => onChange({ ...node, pathsChanged: v })} />
            <CommitMessageField value={node.commitMessage} onChange={v => onChange({ ...node, commitMessage: v })} />
            <TagField value={node.tag} onChange={v => onChange({ ...node, tag: v })} />
            <ManualField value={node.manual} onChange={v => onChange({ ...node, manual: v })} />
            <ScheduleField value={node.schedule} onChange={v => onChange({ ...node, schedule: v })} />
        </div>
    );
}

// --- Felder für jede Bedingung ---

function BranchField({ value, onChange }: { value?: string; onChange: (v?: string) => void }) {
    return (
        <div>
            <label>Branch (Name or Pattern): </label>
            <input
                type="text"
                value={value ?? ""}
                placeholder="like main or release-*"
                onChange={e => onChange(e.target.value || undefined)}
                style={{ margin: 4 }}
            />
        </div>
    );
}

function PathsChangedField({ value, onChange }: { value?: string[]; onChange: (v?: string[]) => void }) {
    return (
        <div>
            <label>Changed paths: </label>
            <input
                type="text"
                value={value?.join(", ") ?? ""}
                placeholder="like src/, config/"
                onChange={e => {
                    if (e.target.value.endsWith(", ,")) {
                        return;
                    }
                    if (e.target.value.endsWith(",") && (value?.join(", ") ?? "").endsWith(" ")) {
                        onChange(value?.slice(0, -1));
                        return;
                    }
                    onChange(e.target.value.replaceAll(", ,", ",").split(",").map(s => s.trim()))}
                }
                style={{ margin: 4 }}
            />
        </div>
    );
}

function CommitMessageField({
                                value,
                                onChange,
                            }: {
    value?: { includes?: string; excludes?: string; regex?: string };
    onChange: (v?: { includes?: string; excludes?: string; regex?: string }) => void;
}) {
    return (
        <div>
            <label>Commit-Message: </label>
            <input
                type="text"
                value={value?.includes ?? ""}
                placeholder="Contains..."
                onChange={e => onChange({ ...value, includes: e.target.value || undefined })}
                style={{ margin: 4 }}
            />
            <input
                type="text"
                value={value?.excludes ?? ""}
                placeholder="Doesn't Contain..."
                onChange={e => onChange({ ...value, excludes: e.target.value || undefined })}
                style={{ margin: 4 }}
            />
            <input
                type="text"
                value={value?.regex ?? ""}
                placeholder="Regex"
                onChange={e => onChange({ ...value, regex: e.target.value || undefined })}
                style={{ margin: 4 }}
            />
        </div>
    );
}

function TagField({
                      value,
                      onChange,
                  }: {
    value?: { matches: string };
    onChange: (v?: { matches: string }) => void;
}) {
    return (
        <div>
            <label>Tag (Pattern): </label>
            <input
                type="text"
                value={value?.matches ?? ""}
                placeholder="like release-*, v*"
                onChange={e => onChange(e.target.value ? { matches: e.target.value } : undefined)}
                style={{ margin: 4 }}
            />
        </div>
    );
}

function ManualField({ value, onChange }: { value?: boolean; onChange: (v?: boolean) => void }) {
    return (
        <div>
            <label>Manuell auslösbar: </label>
            <input
                type="checkbox"
                checked={!!value}
                onChange={e => onChange(e.target.checked ? true : undefined)}
                style={{ margin: 4 }}
            />
        </div>
    );
}

function ScheduleField({ value, onChange }: { value?: string; onChange: (v?: string) => void }) {
    return (
        <div>
            <label>Schedule (Cron): </label>
            <input
                type="text"
                value={value ?? ""}
                placeholder="like 0 0 * * *"
                onChange={e => onChange(e.target.value || undefined)}
                style={{ margin: 4 }}
            />
        </div>
    );
}